import { produce } from 'immer';
import { isString, isNil, includes } from 'lodash/fp';
import type { TypedReduxActionOutcomes } from 'app/store/storeTypes';
import { REHYDRATE } from 'app/store/types';
import { DEFAULT_TAB, TabId, InspectorTabId, ProjectTabId } from './constants';
import {
    SELECT_WORKBENCH_DATASET_TAB,
    TOGGLE_MAXIMIZED_PREVIEW,
    SELECT_WORKBENCH_PROJECT_TAB,
    SELECT_WORKBENCH_GROUP_TAB,
    SELECT_WORKBENCH_INSPECTOR_TAB,
    SELECT_PROJECT_INSPECTOR_TAB,
    TOGGLE_AUTORUN_PREVIEW
} from './types';

export type PreferencesSlice = {
    workbenchDatasetTab?:
        | string
        | {
              default: string;
              byEntityId: Record<string, string>;
          };
    workbenchProjectTab: ProjectTabId;
    workbenchGroupTab: TabId;
    workbenchInspectorTab: InspectorTabId;
    projectInspectorTab: InspectorTabId;
    isPreviewMaximized: boolean;
    isAutorunPreview: boolean;
};

export const INITIAL_STATE: PreferencesSlice = {
    workbenchDatasetTab: {
        default: DEFAULT_TAB,
        byEntityId: {}
    },
    workbenchProjectTab: ProjectTabId.OUTPUT_TABLES,
    workbenchGroupTab: TabId.OUTPUTS,
    workbenchInspectorTab: InspectorTabId.TRANSFORM,
    projectInspectorTab: InspectorTabId.INFO,
    isPreviewMaximized: false,
    isAutorunPreview: true
};

export const preferencesReducer = produce((draft, action: TypedReduxActionOutcomes) => {
    switch (action.type) {
        case TOGGLE_MAXIMIZED_PREVIEW:
            draft.isPreviewMaximized = !draft.isPreviewMaximized;
            break;
        case TOGGLE_AUTORUN_PREVIEW:
            draft.isAutorunPreview = !draft.isAutorunPreview;
            break;
        case SELECT_WORKBENCH_DATASET_TAB:
            // cheap migration if localstorage has old version
            if (isString(draft.workbenchDatasetTab) || isNil(draft.workbenchDatasetTab)) {
                draft.workbenchDatasetTab = {
                    default: DEFAULT_TAB,
                    byEntityId: {}
                };
            }

            if (includes(action.payload.tab, ['preview', 'columns', 'profile', 'sql'])) {
                draft.workbenchDatasetTab.default = action.payload.tab;
            }

            draft.workbenchDatasetTab.byEntityId[action.payload.entityId] = action.payload.tab;
            break;
        case SELECT_WORKBENCH_PROJECT_TAB:
            draft.workbenchProjectTab = action.payload.tab;
            break;
        case SELECT_WORKBENCH_GROUP_TAB:
            draft.workbenchGroupTab = action.payload.tab;
            break;
        case SELECT_WORKBENCH_INSPECTOR_TAB:
            draft.workbenchInspectorTab = action.payload.tab;
            break;
        case SELECT_PROJECT_INSPECTOR_TAB: {
            draft.projectInspectorTab = action.payload.tab;
            break;
        }
        case REHYDRATE: {
            const { payload } = action;
            if ('preferences' in payload) {
                Object.assign(draft, payload.preferences);
            }
            break;
        }
        default:
            break;
    }
}, INITIAL_STATE);
